// pages/About.js
import { useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/FooterCTA";
import FlipCards from "../components/FlipCards";
import Projects from "../components/Projects";
import HardwareSection from "../animate_comp/HardwareSection";
import Container from "../components/Container";
import { ShieldCheck, Zap, Award } from 'lucide-react'; // Make sure lucide-react is installed


/* ---------- shared scroll-reveal hook ---------- */
function useReveal(threshold = 0.2) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, visible];
}

/* ============================================================
   HERO
   ============================================================ */
function Hero() {
  const [path, setPath] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setPath(1), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="w-full relative py-8 sm:py-12 lg:py-16">
      {/* faint grid backdrop */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#7c3aed 1px, transparent 1px), linear-gradient(90deg, #7c3aed 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      {/* corner pads */}
      <div className="absolute top-6 left-6 w-1.5 h-1.5 rounded-full bg-[#e83e8c] hidden sm:block" />
      <div className="absolute top-6 right-6 w-1.5 h-1.5 rounded-full bg-[#7c3aed] hidden sm:block" />

      <div className="relative max-w-5xl mx-auto text-center">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#0f0f0f] max-w-3xl mx-auto leading-[1.1] tracking-tight">
          Engineering ideas into{" "}
          <span className="relative inline-block">
            <span className="bg-gradient-to-r from-[#e83e8c] to-[#7c3aed] bg-clip-text text-transparent">
              reality
            </span>
            <svg
              className="absolute -bottom-2 left-0 w-full"
              height="8"
              viewBox="0 0 200 8"
              preserveAspectRatio="none"
            >
              <path
                d="M0,4 Q50,0 100,4 T200,4"
                stroke="#7c3aed"
                strokeWidth="2"
                fill="none"
                strokeDasharray="200"
                strokeDashoffset={path ? 0 : 200}
                style={{
                  transition: "stroke-dashoffset 1.2s ease-out 0.3s",
                }}
              />
            </svg>
          </span>
        </h1>

        <p className="mt-3 sm:mt-4 text-[#666] text-xs sm:text-sm lg:text-base max-w-xl mx-auto leading-relaxed sm:leading-7 px-4 sm:px-0">

          We bring mechanical, electronics, embedded, and software engineering together to turn ideas into reliable, scalable, and production-ready products.
          From the first concept to the final product, we focus on three things that define how we work:


        </p>

        {/* live oscilloscope strip — signature motion element */}
        <div className="mt-6 sm:mt-8 max-w-md mx-auto h-12 sm:h-14 lg:h-16 rounded-lg bg-white overflow-hidden relative">
          <svg viewBox="0 0 400 60" className="w-full h-full">
            <polyline
              points="0,30 20,30 30,10 40,50 50,10 60,30 90,30 100,15 110,45 120,30 150,30 160,5 170,55 180,30 220,30 230,18 240,42 250,30 400,30"
              fill="none"
              stroke="#7c3aed"
              strokeWidth="1.5"
              className="animate-[scope_3s_linear_infinite]"
            />
          </svg>
          <div className="absolute inset-0 bg-gradient-to-r from-purple-50 via-transparent to-purple-50" />
        </div>
      </div>

      <style>{`
        @keyframes scope {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}

/* ============================================================
   STAT STRIP (ticking counters)
   ============================================================ */
function StatStrip() {
  const [ref, visible] = useReveal(0.3);
  const stats = [
    { value: 120, suffix: "+", label: "Projects shipped" },
    { value: 48, suffix: "hr", label: "Avg. prototype turnaround" },
    { value: 99, suffix: "%", label: "On-spec tolerance rate" },
    { value: 3, suffix: "yrs", label: "In hardware R&D" },
  ];
  return (
    <section
      id="u2"
      ref={ref}
      className="bg-gradient-to-r from-[#e83e8c] to-[#7c3aed] w-full rounded-xl sm:rounded-2xl py-6 sm:py-8 lg:py-10"
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {stats.map((s, i) => (
          <Counter key={s.label} {...s} active={visible} delay={i * 120} />
        ))}
      </div>
    </section>
  );
}

function Counter({ value, suffix, label, active, delay }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    const start = performance.now() + delay;
    let frame;
    const tick = (now) => {
      const elapsed = now - start;
      if (elapsed < 0) {
        frame = requestAnimationFrame(tick);
        return;
      }
      const progress = Math.min(elapsed / 900, 1);
      setN(Math.floor(progress * value));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, value, delay]);

  return (
    <div className="text-center px-2 sm:px-0">
      <div className="font-mono text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-white">
        {n}
        <span className="text-white/70">{suffix}</span>
      </div>
      <div className="text-white/80 text-[9px] sm:text-xs lg:text-sm mt-0.5 uppercase tracking-wide">
        {label}
      </div>
    </div>
  );
}
export function Values() {
  const [ref, visible] = useReveal(0.2);

  const values = [
    {
      title: "Quality",
      desc: "Precision in every detail",
      icon: ShieldCheck,
    },
    {
      title: "Speed",
      desc: "Rapid execution, on-time delivery",
      icon: Zap,
    },
    {
      title: "Excellence",
      desc: "Delivering beyond expectations.",
      icon: Award,
    },
  ];

  return (
    <section
      id="u3"
      ref={ref}
      className="relative w-full overflow-hidden px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24"
    >
      {/* ================= HEADER ================= */}
      <div className="relative z-10 mx-auto mb-12 max-w-3xl text-center sm:mb-14">
        <p className="mb-2 text-sm font-semibold uppercase tracking-[0.25em] text-purple-500">
          What Drives Us
        </p>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Our Values
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed">
          We Innovate, Design and Deliver Products for you
        </p>
      </div>

      {/* ================= VALUES GRID ================= */}
      <div
        className={`relative z-10 mx-auto grid max-w-6xl grid-cols-1 gap-6 sm:grid-cols-3 transition-opacity duration-700 ${visible ? "opacity-100" : "opacity-0"
          }`}
      >
        {values.map((v, i) => {
          const Icon = v.icon;
          return (
            <div
              key={v.title}
              className="rounded-2xl border border-purple-100 bg-purple-50 p-6 sm:p-7"
            >
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-purple-200/70 text-purple-700">
                <Icon className="h-6 w-6" />
              </div>

              <h3 className="mb-2 text-lg font-semibold text-slate-900">
                {v.title}
              </h3>

              <p className="text-sm leading-6 text-slate-600">
                {v.desc}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
/* ============================================================
   FOUNDERS & TESTIMONIALS (6 Equal Cards Grid)
   ============================================================ */
export function FoundersWords() {
  const [ref, visible] = useReveal(0.3);

  return (
    <section
      id="u9"
      ref={ref}
      className="w-full bg-white py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0f0f0f] tracking-tight">
            Co-Founders <span className="text-[#7c3aed]">Words</span> & Client Reviews
          </h2>
          <div className="w-16 h-1 bg-gradient-to-r from-[#7c3aed] to-[#e83e8c] mx-auto rounded-full mt-3" />
        </div>

        {/* 6-Card Unified Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {/* Card 1: Founder 1 */}
          <div
            className={`group relative flex flex-col justify-between rounded-2xl border border-purple-100 bg-purple-50/30 p-6 sm:p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-purple-300 hover:shadow-xl hover:shadow-purple-500/10 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
            style={{ transitionDelay: "0ms" }}
          >
            <div>
              <svg width="32" height="24" className="text-[#b54cf1] mb-4" fill="currentColor" viewBox="0 0 36 28">
                <path d="M0 28V16.8C0 7.2 5.6 1.2 14.4 0l1.6 4.4C10.8 5.6 8 8.8 8 13.2h6.4V28H0zm19.6 0V16.8c0-9.6 5.6-15.6 14.4-16.8L35.6 4.4c-5.2 1.2-8 4.4-8 8.8h6.4V28H19.6z" />
              </svg>
              <p className="text-[#0f0f0f] text-sm sm:text-base font-medium leading-relaxed">
                Founding KeyMech Designs came from a passion for precision and purpose-built design. Every product we create reflects our belief that great mechanics and great craftsmanship should go hand in hand
              </p>
            </div>
            <div className="mt-6 flex items-center gap-3 pt-4 border-t border-purple-100/60">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#e83e8c] to-[#cc3ff0] flex items-center justify-center text-white text-xs font-semibold shadow-sm">
                UA
              </div>
              <div>
                <div className="text-[#0f0f0f] text-sm font-semibold">Umar Ali</div>
                <div className="text-purple-600 text-xs font-mono">Chief Executive Officer</div>
              </div>
            </div>
          </div>

          {/* Card 2: Founder 2 */}
          <div
            className={`group relative flex flex-col justify-between rounded-2xl border border-purple-100 bg-purple-50/30 p-6 sm:p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-purple-300 hover:shadow-xl hover:shadow-purple-500/10 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
            style={{ transitionDelay: "150ms" }}
          >
            <div>
              <svg width="32" height="24" className="text-[#7c3aed] mb-4" fill="currentColor" viewBox="0 0 36 28">
                <path d="M0 28V16.8C0 7.2 5.6 1.2 14.4 0l1.6 4.4C10.8 5.6 8 8.8 8 13.2h6.4V28H0zm19.6 0V16.8c0-9.6 5.6-15.6 14.4-16.8L35.6 4.4c-5.2 1.2-8 4.4-8 8.8h6.4V28H19.6z" />
              </svg>
              <p className="text-[#0f0f0f] text-sm sm:text-base font-medium leading-relaxed">
                From initial concept to final product, we are with you at ever step.KeyMech brings every discipline together to take your vision from concept to market-ready reality.
              </p>
            </div>
            <div className="mt-6 flex items-center gap-3 pt-4 border-t border-purple-100/60">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#e83e8c] to-[#7c3aed] flex items-center justify-center text-white text-xs font-semibold shadow-sm">
                MK
              </div>
              <div>
                <div className="text-[#0f0f0f] text-sm font-semibold">Mubassar Khan</div>
                <div className="text-purple-600 text-xs font-mono">Chief Operating Officer</div>
              </div>
            </div>
          </div>

          {/* Card 3: Founder 3 */}
          <div
            className={`group relative flex flex-col justify-between rounded-2xl border border-purple-100 bg-purple-50/30 p-6 sm:p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-purple-300 hover:shadow-xl hover:shadow-purple-500/10 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
            style={{ transitionDelay: "300ms" }}
          >
            <div>
              <svg width="32" height="24" className="text-[#7c3aed] mb-4" fill="currentColor" viewBox="0 0 36 28">
                <path d="M0 28V16.8C0 7.2 5.6 1.2 14.4 0l1.6 4.4C10.8 5.6 8 8.8 8 13.2h6.4V28H0zm19.6 0V16.8c0-9.6 5.6-15.6 14.4-16.8L35.6 4.4c-5.2 1.2-8 4.4-8 8.8h6.4V28H19.6z" />
              </svg>
              <p className="text-[#0f0f0f] text-sm sm:text-base font-medium leading-relaxed">
                KeyMech Designs is a team of passionate engineers, designers, and innovators dedicated to turning ideas into practical, high-quality products
              </p>
            </div>
            <div className="mt-6 flex items-center gap-3 pt-4 border-t border-purple-100/60">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#e83e8c] to-[#7c3aed] flex items-center justify-center text-white text-xs font-semibold shadow-sm">
                SA
              </div>
              <div>
                <div className="text-[#0f0f0f] text-sm font-semibold">Saad Afzal</div>
                <div className="text-purple-600 text-xs font-mono">Chief Technology Officer</div>
              </div>
            </div>
          </div>





          {/* Card 6: Image 3 - FIXED: Actually render the image */}


        </div>
      </div>
    </section>
  );
}

/* ============================================================
   CLOSING CTA
   ============================================================ */
function ClosingCTA() {
  const [ref, visible] = useReveal(0.3);
  return (
    <section
      id="u10"
      ref={ref}
      className="w-full relative bg-gradient-to-br from-white via-[#faf7ff] to-purple-50 py-10 sm:py-14 lg:py-16 overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#7c3aed 1px, transparent 1px), linear-gradient(90deg, #7c3aed 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      <div
        className={`relative max-w-3xl mx-auto text-center px-4 sm:px-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
      >
        <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#0f0f0f] leading-tight">
          Have a hardware idea that needs{" "}
          <span className="bg-gradient-to-r from-[#e83e8c] to-[#7c3aed] bg-clip-text text-transparent">
            a working prototype?
          </span>
        </h2>

        <p className="mt-3 sm:mt-4 text-[#666] text-xs sm:text-sm lg:text-base max-w-xl leading-relaxed mx-auto">
          We specialize in in industrial design, Eletronics & embedded system, PCB Design, Frimware development.
        </p>

        <a
          href="/contact"
          className="group inline-flex items-center gap-2 mt-5 sm:mt-6 lg:mt-8 bg-gradient-to-r from-[#e83e8c] to-[#7c3aed] text-white font-semibold text-xs sm:text-sm px-5 sm:px-6 py-2.5 sm:py-3 rounded-full hover:opacity-90 transition-opacity duration-300"
        >
          Start a Project
          <span className="transition-transform duration-300 group-hover:translate-x-1">
            →
          </span>
        </a>
      </div>
    </section>
  );
}

/* ============================================================
   PAGE ASSEMBLY
   ============================================================ */
/* ============================================================
   PAGE ASSEMBLY  — fixed version
   ============================================================ */
function About({ openForm }) {
  return (
    <div className="w-full bg-white overflow-x-hidden">
 <Container>
      <Navbar openForm={openForm} />

      {/* SINGLE CONTAINER FOR ALL COMPONENTS */}

      {/*
          REMOVED both spacer divs that used to sit here:
            <div className="h-0.5 sm:h-1"></div>
            <div className="mt-20px"></div>
          - "mt-20px" is not a valid Tailwind class (needs mt-[20px] syntax),
            so it was contributing 0px of spacing.
          - The h-0.5/h-1 div was only 2-4px, nowhere near enough to clear
            a fixed navbar.
          - HardwareSection.css now owns the navbar-clearance spacing via
            margin-top on .hw__sticky at every breakpoint, so stacking a
            second spacer here would just double it up (or, since it was
            broken, silently do nothing). Single source of truth = fewer
            surprises.

          If Navbar is NOT position: fixed (i.e. it's a normal in-flow
          element or position: sticky itself), you likely don't need any
          margin here or in HardwareSection.css at all — let me know how
          Navbar is positioned and I'll adjust.
        */}
     
        <div>
          <HardwareSection />

          <FoundersWords />
        </div>
        <div className="mt-6 sm:mt-8 lg:mt-10">
          <Projects />
        </div>
        <div className="mt-6 sm:mt-8 lg:mt-10">
          <FlipCards />
        </div>

        <div className="mt-6 sm:mt-8 lg:mt-10 bg-purple-400">
          <Values />
        </div>
        <div className="mt-6 sm:mt-8 lg:mt-10">
          <ClosingCTA />
        </div>

      </Container>

      <Footer />

    </div>
  );
}

export default About;